import React, { useState } from "react";
import PropTypes from "prop-types";
import { ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { SFExpandMoreIcon, SFDragHandleIcon } from "../../../icons";
import { SFGridContainer, SFGridItem } from "../../../grid";
import getStyled from "../../../../hooks/getStyled";
import ActionButtons from "./actionButtons";
import SFTooltip from "../../../tooltip";

const { styled } = getStyled();

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
  transform: "rotate(0deg)",
  transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "&.open": {
    transform: "rotate(180deg)",
  },
}));

const SFGridItemStyled = styled(SFGridItem)(({ theme, uncappedWidth }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  ...(uncappedWidth && { maxWidth: "100% !important" }),
}));

// This function makes it possible to display the block drop effect when dragging
// an external item over a tree node that is configured not to accept external items.
const onDragOver = (event) => {
  event.dataTransfer.dropEffect = "none";
  event.preventDefault();
};

const ListItemButtonRenderer = ({
  buttonDefs,
  node,
  onClick,
  options,
  selected,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  let typographyVariant = "tableList";
  if (!node.data.disabled) {
    if (selected || isHovered) {
      typographyVariant = "tableListSelected";
    }
  } else {
    typographyVariant = "tableListDisabled";
  }

  return (
    <ListItemButton
      selected={selected}
      disableGutters
      disableRipple={node.data.disabled}
      sx={{
        "&:hover": {
          backgroundColor: "transparent",
        },
        height: "40px",
      }}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragOver={(event) => onDragOver(event)}
    >
      <SFGridContainer
        alignItems="center"
        justifyContent="space-between"
        spacing={0}
      >
        <SFGridItemStyled xs={buttonDefs.length > 0 && isHovered ? 9 : 12}>
          <div
            style={{
              width: `${16 * node.data.depth}px`,
              minWidth: `${16 * node.data.depth}px`,
            }}
          ></div>
          {options.draggable && (
            <span
              ref={options.handleRef}
              style={{ display: "inline-block", width: "20px", height: "20px" }}
            >
              <SFDragHandleIcon size="default" color="grey350" />
            </span>
          )}
          {options.hasChild ? (
            <ListItemIconStyled
              onClick={options.onToggle}
              className={options.isOpen ? "open" : ""}
            >
              <SFExpandMoreIcon size="medium" color="grey350" />
            </ListItemIconStyled>
          ) : (
            <div
              style={{
                width: "24px",
                minWidth: "24px",
              }}
            ></div>
          )}
          <SFTooltip content={node.text} placement="bottom-start">
            <ListItemText
              {...(!node.data.disabled && { onClick: onClick })}
              primaryTypographyProps={{
                variant: typographyVariant,
                noWrap: true,
              }}
            >
              {node.text}
            </ListItemText>
          </SFTooltip>
        </SFGridItemStyled>
        {buttonDefs.length > 0 && isHovered && (
          <SFGridItem xs={3}>
            <ActionButtons buttonDefs={buttonDefs} />
          </SFGridItem>
        )}
      </SFGridContainer>
    </ListItemButton>
  );
};

ListItemButtonRenderer.propTypes = {
  buttonDefs: PropTypes.array,
  node: PropTypes.shape({
    data: PropTypes.shape({
      depth: PropTypes.number,
      disabled: PropTypes.bool,
    }),
    text: PropTypes.string,
  }),
  onClick: PropTypes.func,
  options: PropTypes.shape({
    draggable: PropTypes.bool,
    handleRef: PropTypes.object,
    hasChild: PropTypes.bool,
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
  }),
  selected: PropTypes.bool,
};

export default ListItemButtonRenderer;
